import { ApplicationConfig, LOCALE_ID, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';
import { provideHttpClient, withFetch } from '@angular/common/http';
import {
  PreloadAllModules,
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle';
import { provideToastr } from 'ngx-toastr';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';

import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { initializeAppCheck, provideAppCheck, ReCaptchaEnterpriseProvider } from '@angular/fire/app-check';
import { connectAuthEmulator, getAuth, provideAuth } from '@angular/fire/auth';
import { connectFirestoreEmulator, getFirestore, provideFirestore } from '@angular/fire/firestore';
import { connectFunctionsEmulator, getFunctions, provideFunctions } from '@angular/fire/functions';
import { connectStorageEmulator, getStorage, provideStorage } from '@angular/fire/storage';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { FUNCTIONS_REGION } from '@parkingless/interface';
import { EMULATOR_HOST, EMULATOR_PORTS } from './config/emulator.config';
import { ItDateAdapter, IT_DATE_FORMATS } from './services/date.service';
import { ItPaginatorIntl } from './services/ui/paginator-intl';
import { AppCheckGateService } from './services/app-check-gate.service';

registerLocaleData(localeIt);

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'it' },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    // The toggles are styled as segmented controls (_console.scss), where the chosen
    // option is shown by being lifted onto the track; a checkmark in it as well pushes
    // the label off centre and says the same thing twice.
    {
      provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
      useValue: { hideSingleSelectionIndicator: true, hideMultipleSelectionIndicator: true },
    },
    provideToastr({
      positionClass: 'toast-bottom-right',
      timeOut: 4000,
      progressBar: true,
      closeButton: true,
    }),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      withComponentInputBinding(),
      // Every page is a lazy chunk, and a page's chunk asks for its own imports only
      // once it has arrived: fetched on the click, that is two round trips before
      // anything renders (0.5 s on an edge the release has just emptied). Fetched
      // after the first navigation instead, the click finds them in memory.
      withPreloading(PreloadAllModules),
    ),
    provideHttpClient(withFetch()),

    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    // App Check, before anything that sends a request: Functions and Storage take the
    // token from whatever is registered when they initialise (Firestore alone tolerates
    // a late registration). Skipped when the environment carries no key — the emulator
    // configuration, and a real one whose key is not set up yet — so the bundle never
    // holds a fake key and the SDK never fetches a token that cannot succeed. A
    // development build against the dev project attests through a debug token that
    // main.ts arranges.
    ...(environment.appCheck
      ? [
          provideAppCheck(() =>
            initializeAppCheck(getApp(), {
              provider: new ReCaptchaEnterpriseProvider(environment.appCheck!.recaptchaEnterpriseSiteKey),
              isTokenAutoRefreshEnabled: true,
            }),
          ),
        ]
      : []),
    // The App Check boot gate: bootstrap, and with it the first navigation and its
    // guards, waits until the first round of attempts has an answer, so nothing reads
    // Firestore or calls a function before a token is held. Instant with no provider
    // registered (above) or with the token the SDK persisted on an earlier load;
    // index.html shows a static splash for the rare real wait. AppComponent shows the
    // blocked page if the answer is no.
    provideAppInitializer(() => inject(AppCheckGateService).settled),
    provideAuth(() => {
      const auth = getAuth();
      if (environment.useEmulators) {
        // `disableWarnings` only silences the banner the SDK prints into the console;
        // the e2e run asserts a clean console, and that warning is expected noise.
        connectAuthEmulator(auth, `http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}`, {
          disableWarnings: true,
        });
      }
      return auth;
    }),
    provideFirestore(() => {
      const firestore = getFirestore();
      if (environment.useEmulators) {
        connectFirestoreEmulator(firestore, EMULATOR_HOST, EMULATOR_PORTS.firestore);
      }
      return firestore;
    }),
    // Every callable this app uses is deployed to europe-west3. A wrong region does
    // not fail at build time — it fails at call time, so it is pinned here.
    provideFunctions(() => {
      const functions = getFunctions(undefined, FUNCTIONS_REGION);
      if (environment.useEmulators) {
        connectFunctionsEmulator(functions, EMULATOR_HOST, EMULATOR_PORTS.functions);
      }
      return functions;
    }),
    provideStorage(() => {
      const storage = getStorage();
      if (environment.useEmulators) {
        connectStorageEmulator(storage, EMULATOR_HOST, EMULATOR_PORTS.storage);
      }
      return storage;
    }),

    // Italian date parsing/formatting for every Material datepicker.
    { provide: DateAdapter, useClass: ItDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: IT_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'it-IT' },
    // Material reserves exactly one line under a field for its hint or error. Several of
    // ours wrap to two at narrow column widths, and the overflow rendered on top of the
    // next row. Dynamic sizing grows the subscript to fit — and takes no space at all on
    // the many fields that have neither a hint nor an error.
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { subscriptSizing: 'dynamic' },
    },
    // Material's paginator ignores LOCALE_ID; without this it is the one English
    // fragment left in the console.
    { provide: MatPaginatorIntl, useClass: ItPaginatorIntl },
  ],
};
